import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getEmployeeSalaryList, setEmployeeSalary, getEmployeeAttendance, markEmployeeAttendance, getEmployeeFines, addEmployeeFine, deleteEmployeeFine, generatePayroll, markPayrollPaid, getPayrollHistory } from '../services/api';
import { showSuccess, showError, showConfirmDanger } from '../services/swal';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const EmployeeSalary = () => {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Modals
    const [showSalaryModal, setShowSalaryModal] = useState(false);
    const [showFineModal, setShowFineModal] = useState(false);
    const [showAttendanceModal, setShowAttendanceModal] = useState(false);
    const [showPayrollModal, setShowPayrollModal] = useState(false);

    // Form states
    const [salaryForm, setSalaryForm] = useState({ user_id: '', base_salary: '' });
    const [fineForm, setFineForm] = useState({ user_id: '', amount: '', reason: '', fine_date: new Date().toISOString().split('T')[0] });
    const [attendanceForm, setAttendanceForm] = useState({ user_id: '', date: new Date().toISOString().split('T')[0], status: 'present', check_in: '', check_out: '', notes: '' });
    const [payrollForm, setPayrollForm] = useState({ user_id: '', bonus: 0, deductions: 0 });

    // Data
    const [attendance, setAttendance] = useState([]);
    const [fines, setFines] = useState([]);
    const [payrollHistory, setPayrollHistory] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchEmployees = useCallback(async () => {
        try {
            const res = await getEmployeeSalaryList();
            setEmployees(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

    const fetchAttendance = useCallback(async () => {
        try {
            const res = await getEmployeeAttendance({ month: selectedMonth, year: selectedYear, user_id: selectedEmployee?.id });
            setAttendance(res.data);
        } catch (err) { console.error(err); }
    }, [selectedMonth, selectedYear, selectedEmployee]);

    const fetchFines = useCallback(async () => {
        try {
            const res = await getEmployeeFines({ month: selectedMonth, year: selectedYear, user_id: selectedEmployee?.id });
            setFines(res.data);
        } catch (err) { console.error(err); }
    }, [selectedMonth, selectedYear, selectedEmployee]);

    const fetchPayroll = useCallback(async () => {
        try {
            const res = await getPayrollHistory({ month: selectedMonth, year: selectedYear });
            setPayrollHistory(res.data);
        } catch (err) { console.error(err); }
    }, [selectedMonth, selectedYear]);

    useEffect(() => {
        if (activeTab === 'attendance') fetchAttendance();
        if (activeTab === 'fines') fetchFines();
        if (activeTab === 'payroll') fetchPayroll();
    }, [activeTab, fetchAttendance, fetchFines, fetchPayroll]);

    const handleSetSalary = async () => {
        setActionLoading(true);
        try {
            await setEmployeeSalary(salaryForm);
            showSuccess('Salary Updated');
            setShowSalaryModal(false);
            fetchEmployees();
        } catch (err) { showError('Failed', err.response?.data?.message); }
        finally { setActionLoading(false); }
    };

    const handleAddFine = async () => {
        setActionLoading(true);
        try {
            await addEmployeeFine(fineForm);
            showSuccess('Fine Added');
            setShowFineModal(false);
            fetchFines();
            fetchEmployees();
        } catch (err) { showError('Failed', err.response?.data?.message); }
        finally { setActionLoading(false); }
    };

    const handleDeleteFine = async (id) => {
        const r = await showConfirmDanger('Remove Fine?', 'This action cannot be undone.');
        if (!r.isConfirmed) return;
        try {
            await deleteEmployeeFine(id);
            showSuccess('Fine Removed');
            fetchFines();
            fetchEmployees();
        } catch (err) { showError('Failed', err.response?.data?.message); }
    };

    const handleMarkAttendance = async () => {
        setActionLoading(true);
        try {
            await markEmployeeAttendance(attendanceForm);
            showSuccess('Attendance Marked');
            setShowAttendanceModal(false);
            fetchAttendance();
            fetchEmployees();
        } catch (err) { showError('Failed', err.response?.data?.message); }
        finally { setActionLoading(false); }
    };

    const handleGeneratePayroll = async () => {
        setActionLoading(true);
        try {
            await generatePayroll({ ...payrollForm, month: selectedMonth, year: selectedYear });
            showSuccess('Payroll Generated');
            setShowPayrollModal(false);
            fetchPayroll();
        } catch (err) { showError('Failed', err.response?.data?.message); }
        finally { setActionLoading(false); }
    };

    const handleMarkPaid = async (id) => {
        try {
            await markPayrollPaid(id);
            showSuccess('Marked as Paid');
            fetchPayroll();
        } catch (err) { showError('Failed', err.response?.data?.message); }
    };

    const tabs = [
        { id: 'overview', label: 'Team Overview', icon: 'bi-people-fill' },
        { id: 'attendance', label: 'Attendance', icon: 'bi-calendar-check' },
        { id: 'fines', label: 'Fines', icon: 'bi-exclamation-triangle' },
        { id: 'payroll', label: 'Payroll', icon: 'bi-cash-stack' },
    ];

    if (loading) return <Layout title="Payroll & HR"><div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary"></div></div></Layout>;

    return (
        <Layout title="Payroll & HR">
            {/* Header */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div>
                    <h4 className="fw-bold mb-0">Employee Management</h4>
                    <small className="text-muted">Salary, Attendance & Fines</small>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <select className="form-select rounded-pill" style={{ width: 'auto' }} value={selectedMonth} onChange={e => setSelectedMonth(+e.target.value)}>
                        {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                    <select className="form-select rounded-pill" style={{ width: 'auto' }} value={selectedYear} onChange={e => setSelectedYear(+e.target.value)}>
                        {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
            </div>

            {/* Tabs */}
            <div className="d-flex gap-2 mb-4 flex-wrap">
                {tabs.map(t => (
                    <button key={t.id} className={`btn rounded-pill px-4 py-2 fw-bold ${activeTab === t.id ? 'btn-primary' : 'btn-light border'}`} onClick={() => setActiveTab(t.id)}>
                        <i className={`bi ${t.icon} me-2`}></i>{t.label}
                    </button>
                ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className="row g-3">
                    {employees.length === 0 ? (
                        <div className="col-12 text-center py-5">
                            <i className="bi bi-people fs-1 opacity-25"></i>
                            <h5 className="mt-3">No team members found</h5>
                            <p className="text-muted">Invite employees from Team Management first.</p>
                        </div>
                    ) : employees.map(emp => (
                        <div className="col-12 col-md-6 col-xl-4" key={emp.id}>
                            <div className="card border-0 shadow-sm rounded-4 p-4 h-100">
                                <div className="d-flex align-items-center gap-3 mb-3">
                                    <div style={{ width: 50, height: 50, borderRadius: 16, background: 'linear-gradient(135deg, var(--primary-color), #8B78E6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                                        {emp.name?.charAt(0)}
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="fw-bold">{emp.name}</div>
                                        <small className="text-muted text-uppercase">{emp.role}</small>
                                    </div>
                                    <button className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={() => {
                                        setSalaryForm({ user_id: emp.id, base_salary: emp.salary?.base_salary || '' });
                                        setShowSalaryModal(true);
                                    }}>
                                        <i className="bi bi-pencil-square"></i>
                                    </button>
                                </div>
                                <div className="row g-2 text-center">
                                    <div className="col-4">
                                        <div className="p-2 rounded-3 bg-success bg-opacity-10">
                                            <div className="fw-bold text-success">Rs. {emp.salary ? parseFloat(emp.salary.base_salary).toLocaleString() : '0'}</div>
                                            <small className="text-muted" style={{ fontSize: '0.65rem' }}>Base Salary</small>
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="p-2 rounded-3 bg-danger bg-opacity-10">
                                            <div className="fw-bold text-danger">Rs. {parseFloat(emp.fines_this_month || 0).toLocaleString()}</div>
                                            <small className="text-muted" style={{ fontSize: '0.65rem' }}>Fines</small>
                                        </div>
                                    </div>
                                    <div className="col-4">
                                        <div className="p-2 rounded-3 bg-primary bg-opacity-10">
                                            <div className="fw-bold text-primary">{emp.attendance_this_month?.present || 0}d</div>
                                            <small className="text-muted" style={{ fontSize: '0.65rem' }}>Present</small>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3 d-flex gap-2">
                                    <button className="btn btn-sm btn-light border rounded-pill flex-grow-1 fw-bold" onClick={() => { setAttendanceForm({ ...attendanceForm, user_id: emp.id }); setShowAttendanceModal(true); }}>
                                        <i className="bi bi-calendar-plus me-1"></i> Attendance
                                    </button>
                                    <button className="btn btn-sm btn-light border rounded-pill flex-grow-1 fw-bold" onClick={() => { setFineForm({ ...fineForm, user_id: emp.id }); setShowFineModal(true); }}>
                                        <i className="bi bi-exclamation-triangle me-1"></i> Fine
                                    </button>
                                    <button className="btn btn-sm btn-primary rounded-pill flex-grow-1 fw-bold" onClick={() => { setPayrollForm({ ...payrollForm, user_id: emp.id }); setShowPayrollModal(true); }}>
                                        <i className="bi bi-cash me-1"></i> Pay
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Attendance Tab */}
            {activeTab === 'attendance' && (
                <div className="card border-0 shadow-sm rounded-4 p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0">Attendance — {MONTHS[selectedMonth - 1]} {selectedYear}</h6>
                        <div className="d-flex gap-2">
                            <select className="form-select form-select-sm rounded-pill" style={{ width: 'auto' }} value={selectedEmployee?.id || ''} onChange={e => { const emp = employees.find(x => x.id === +e.target.value); setSelectedEmployee(emp || null); }}>
                                <option value="">All Employees</option>
                                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                            </select>
                        </div>
                    </div>
                    {attendance.length === 0 ? (
                        <div className="text-center py-5 text-muted"><i className="bi bi-calendar-x fs-1 opacity-25 d-block mb-2"></i>No records for this period</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead><tr><th>Employee</th><th>Date</th><th>Status</th><th>Check In</th><th>Check Out</th><th>Notes</th></tr></thead>
                                <tbody>
                                    {attendance.map(a => (
                                        <tr key={a.id}>
                                            <td className="fw-bold">{a.user?.name}</td>
                                            <td>{new Date(a.date).toLocaleDateString()}</td>
                                            <td><span className={`badge rounded-pill ${a.status === 'present' ? 'bg-success' : a.status === 'absent' ? 'bg-danger' : a.status === 'half_day' ? 'bg-warning text-dark' : 'bg-info'}`}>{a.status.replace('_',' ')}</span></td>
                                            <td className="text-muted small">{a.check_in || '—'}</td>
                                            <td className="text-muted small">{a.check_out || '—'}</td>
                                            <td className="text-muted small">{a.notes || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Fines Tab */}
            {activeTab === 'fines' && (
                <div className="card border-0 shadow-sm rounded-4 p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0">Fines — {MONTHS[selectedMonth - 1]} {selectedYear}</h6>
                        <select className="form-select form-select-sm rounded-pill" style={{ width: 'auto' }} value={selectedEmployee?.id || ''} onChange={e => { const emp = employees.find(x => x.id === +e.target.value); setSelectedEmployee(emp || null); }}>
                            <option value="">All Employees</option>
                            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                        </select>
                    </div>
                    {fines.length === 0 ? (
                        <div className="text-center py-5 text-muted"><i className="bi bi-check-circle fs-1 opacity-25 d-block mb-2"></i>No fines recorded</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead><tr><th>Employee</th><th>Date</th><th>Amount</th><th>Reason</th><th></th></tr></thead>
                                <tbody>
                                    {fines.map(f => (
                                        <tr key={f.id}>
                                            <td className="fw-bold">{f.user?.name}</td>
                                            <td>{new Date(f.fine_date).toLocaleDateString()}</td>
                                            <td className="fw-bold text-danger">Rs. {parseFloat(f.amount).toLocaleString()}</td>
                                            <td>{f.reason}</td>
                                            <td><button className="btn btn-sm btn-outline-danger rounded-pill" onClick={() => handleDeleteFine(f.id)}><i className="bi bi-trash"></i></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Payroll Tab */}
            {activeTab === 'payroll' && (
                <div className="card border-0 shadow-sm rounded-4 p-4">
                    <h6 className="fw-bold mb-3">Payroll — {MONTHS[selectedMonth - 1]} {selectedYear}</h6>
                    {payrollHistory.length === 0 ? (
                        <div className="text-center py-5 text-muted"><i className="bi bi-cash-coin fs-1 opacity-25 d-block mb-2"></i>No payroll records. Generate from employee cards.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead><tr><th>Employee</th><th>Base</th><th>Fines</th><th>Deductions</th><th>Bonus</th><th>Net Pay</th><th>Status</th><th></th></tr></thead>
                                <tbody>
                                    {payrollHistory.map(p => (
                                        <tr key={p.id}>
                                            <td className="fw-bold">{p.user?.name}</td>
                                            <td>Rs. {parseFloat(p.base_salary).toLocaleString()}</td>
                                            <td className="text-danger">-Rs. {parseFloat(p.total_fines).toLocaleString()}</td>
                                            <td className="text-warning">-Rs. {parseFloat(p.total_deductions).toLocaleString()}</td>
                                            <td className="text-success">+Rs. {parseFloat(p.bonus).toLocaleString()}</td>
                                            <td className="fw-bold fs-5">Rs. {parseFloat(p.net_pay).toLocaleString()}</td>
                                            <td><span className={`badge rounded-pill ${p.status === 'paid' ? 'bg-success' : 'bg-warning text-dark'}`}>{p.status}</span></td>
                                            <td>{p.status !== 'paid' && <button className="btn btn-sm btn-success rounded-pill fw-bold" onClick={() => handleMarkPaid(p.id)}>Mark Paid</button>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Salary Modal */}
            {showSalaryModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={e => e.target === e.currentTarget && setShowSalaryModal(false)}>
                    <div className="modal-dialog modal-dialog-centered"><div className="modal-content border-0 shadow-lg rounded-4">
                        <div className="modal-header border-0 bg-primary text-white p-4"><h5 className="modal-title fw-bold"><i className="bi bi-cash-coin me-2"></i>Set Monthly Salary</h5><button className="btn-close btn-close-white" onClick={() => setShowSalaryModal(false)}></button></div>
                        <div className="modal-body p-4">
                            <label className="form-label small fw-bold text-muted">Base Salary (Rs.)</label>
                            <input type="number" className="form-control" value={salaryForm.base_salary} onChange={e => setSalaryForm({...salaryForm, base_salary: e.target.value})} placeholder="e.g. 25000" />
                        </div>
                        <div className="modal-footer border-0 p-4"><button className="btn btn-primary rounded-pill px-4 fw-bold" onClick={handleSetSalary} disabled={actionLoading}>{actionLoading ? 'Saving...' : 'Save Salary'}</button></div>
                    </div></div>
                </div>
            )}

            {/* Attendance Modal */}
            {showAttendanceModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={e => e.target === e.currentTarget && setShowAttendanceModal(false)}>
                    <div className="modal-dialog modal-dialog-centered"><div className="modal-content border-0 shadow-lg rounded-4">
                        <div className="modal-header border-0 bg-primary text-white p-4"><h5 className="modal-title fw-bold"><i className="bi bi-calendar-check me-2"></i>Mark Attendance</h5><button className="btn-close btn-close-white" onClick={() => setShowAttendanceModal(false)}></button></div>
                        <div className="modal-body p-4">
                            <div className="mb-3"><label className="form-label small fw-bold text-muted">Date</label><input type="date" className="form-control" value={attendanceForm.date} onChange={e => setAttendanceForm({...attendanceForm, date: e.target.value})} /></div>
                            <div className="mb-3"><label className="form-label small fw-bold text-muted">Status</label>
                                <div className="d-flex gap-2 flex-wrap">
                                    {['present','absent','half_day','leave'].map(s => (
                                        <button key={s} className={`btn rounded-pill px-3 fw-bold ${attendanceForm.status === s ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setAttendanceForm({...attendanceForm, status: s})}>{s.replace('_',' ')}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="row g-3">
                                <div className="col-6"><label className="form-label small fw-bold text-muted">Check In</label><input type="time" className="form-control" value={attendanceForm.check_in} onChange={e => setAttendanceForm({...attendanceForm, check_in: e.target.value})} /></div>
                                <div className="col-6"><label className="form-label small fw-bold text-muted">Check Out</label><input type="time" className="form-control" value={attendanceForm.check_out} onChange={e => setAttendanceForm({...attendanceForm, check_out: e.target.value})} /></div>
                            </div>
                            <div className="mt-3"><label className="form-label small fw-bold text-muted">Notes</label><input type="text" className="form-control" value={attendanceForm.notes} onChange={e => setAttendanceForm({...attendanceForm, notes: e.target.value})} placeholder="Optional notes" /></div>
                        </div>
                        <div className="modal-footer border-0 p-4"><button className="btn btn-primary rounded-pill px-4 fw-bold" onClick={handleMarkAttendance} disabled={actionLoading}>{actionLoading ? 'Saving...' : 'Save Attendance'}</button></div>
                    </div></div>
                </div>
            )}

            {/* Fine Modal */}
            {showFineModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={e => e.target === e.currentTarget && setShowFineModal(false)}>
                    <div className="modal-dialog modal-dialog-centered"><div className="modal-content border-0 shadow-lg rounded-4">
                        <div className="modal-header border-0 bg-danger text-white p-4"><h5 className="modal-title fw-bold"><i className="bi bi-exclamation-triangle me-2"></i>Add Fine</h5><button className="btn-close btn-close-white" onClick={() => setShowFineModal(false)}></button></div>
                        <div className="modal-body p-4">
                            <div className="mb-3"><label className="form-label small fw-bold text-muted">Amount (Rs.)</label><input type="number" className="form-control" value={fineForm.amount} onChange={e => setFineForm({...fineForm, amount: e.target.value})} placeholder="e.g. 500" /></div>
                            <div className="mb-3"><label className="form-label small fw-bold text-muted">Reason</label><input type="text" className="form-control" value={fineForm.reason} onChange={e => setFineForm({...fineForm, reason: e.target.value})} placeholder="e.g. Late arrival" /></div>
                            <div className="mb-3"><label className="form-label small fw-bold text-muted">Date</label><input type="date" className="form-control" value={fineForm.fine_date} onChange={e => setFineForm({...fineForm, fine_date: e.target.value})} /></div>
                        </div>
                        <div className="modal-footer border-0 p-4"><button className="btn btn-danger rounded-pill px-4 fw-bold" onClick={handleAddFine} disabled={actionLoading}>{actionLoading ? 'Adding...' : 'Add Fine'}</button></div>
                    </div></div>
                </div>
            )}

            {/* Payroll Modal */}
            {showPayrollModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={e => e.target === e.currentTarget && setShowPayrollModal(false)}>
                    <div className="modal-dialog modal-dialog-centered"><div className="modal-content border-0 shadow-lg rounded-4">
                        <div className="modal-header border-0 bg-success text-white p-4"><h5 className="modal-title fw-bold"><i className="bi bi-cash-stack me-2"></i>Generate Payroll — {MONTHS[selectedMonth - 1]} {selectedYear}</h5><button className="btn-close btn-close-white" onClick={() => setShowPayrollModal(false)}></button></div>
                        <div className="modal-body p-4">
                            <div className="alert bg-primary bg-opacity-10 border-0 rounded-3 p-3 mb-3"><small className="fw-bold"><i className="bi bi-info-circle me-2"></i>Payroll auto-calculates: Base Salary - Fines - Absent Deductions + Bonus</small></div>
                            <div className="mb-3"><label className="form-label small fw-bold text-muted">Bonus (Rs.)</label><input type="number" className="form-control" value={payrollForm.bonus} onChange={e => setPayrollForm({...payrollForm, bonus: e.target.value})} /></div>
                            <div className="mb-3"><label className="form-label small fw-bold text-muted">Extra Deductions (Rs.)</label><input type="number" className="form-control" value={payrollForm.deductions} onChange={e => setPayrollForm({...payrollForm, deductions: e.target.value})} /></div>
                        </div>
                        <div className="modal-footer border-0 p-4"><button className="btn btn-success rounded-pill px-4 fw-bold" onClick={handleGeneratePayroll} disabled={actionLoading}>{actionLoading ? 'Generating...' : 'Generate Payroll'}</button></div>
                    </div></div>
                </div>
            )}
        </Layout>
    );
};

export default EmployeeSalary;
